// Lista de regras com liga/desliga, modo sombra e parâmetros, agrupadas em seções recolhíveis.
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { RuleId } from '@impulso/shared'
import { RULE_CATALOG, type AutopilotPolicyDto, type UpdateAutopilotPolicyBody } from '@impulso/shared/api/autopilot'
import { Switch } from '../../ui'
import './autopilot.css'

const COPILOT: Record<string, string> = { yes: 'O copiloto pode aplicar sozinho', no: 'Sempre pede a sua aprovação', alert: 'Proteção: só avisa e segura o piloto' }

// Grupos pelo que cada regra faz: proteger orçamento e medição, economizar ou fazer render mais
const GROUPS: Array<{ id: string; title: string; desc: string; rules: RuleId[] }> = [
  { id: 'protect', title: 'Proteção', desc: 'Seguram o orçamento e avisam quando algo sai do normal.', rules: ['R8', 'R9', 'R10', 'R11', 'R13'] },
  { id: 'save', title: 'Economia', desc: 'Cortam o que gasta e não traz cliente.', rules: ['R1', 'R3', 'R5', 'R6'] },
  { id: 'grow', title: 'Crescimento', desc: 'Põem mais verba e criatividade no que funciona.', rules: ['R2', 'R4', 'R7', 'R12'] },
]

export default function RulesCard({ policy, canEdit, onChange, saving }: { policy: AutopilotPolicyDto; canEdit: boolean; saving: boolean; onChange: (rules: NonNullable<UpdateAutopilotPolicyBody['rules']>) => void }) {
  const [open, setOpen] = useState<RuleId | null>(null)
  const [draft, setDraft] = useState<Record<string, number>>({})
  return (
    <div className="card">
      <h2>Regras</h2>
      <p className="small muted">O que o Seu Impulso pode fazer por você. "Só observar" registra o que a regra faria, sem agir.</p>
      <div className="stack mt-16" style={{ gap: 10 }}>
        {[...GROUPS, { id: 'other', title: 'Outras', desc: 'Regras novas que ainda não entraram num grupo.', rules: [] as RuleId[] }].map((g) => {
          // regra fora dos grupos (ex.: uma nova no catálogo) cai em "Outras" em vez de sumir da tela
          const rules = policy.rules.filter((r) => g.id === 'other' ? !GROUPS.some((x) => x.rules.includes(r.id)) : g.rules.includes(r.id))
          if (!rules.length) return null
          const on = rules.filter((r) => r.enabled).length
          // grupo com regra observando ou pausada pela equipe já abre, para esse estado não ficar escondido
          const attention = rules.some((r) => r.shadow || r.globallyDisabled || r.globallyShadow)
          return (
            <details className="rule-group" key={g.id} open={attention || undefined}>
              <summary>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="strong">{g.title}</div>
                  <div className="small muted">{g.desc}</div>
                </div>
                <span className={`badge ${on ? 'green' : ''}`}>{on} de {rules.length} ligadas</span>
                <ChevronDown size={16} className="chev" aria-hidden />
              </summary>
              <div className="list">
                {rules.map((r) => {
                  const meta = RULE_CATALOG[r.id]
                  const locked = r.id === 'R11' || r.id === 'R8'
                  const isOpen = open === r.id
                  return (
                    <div className="list-item" key={r.id} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="strong">{meta.title}</div>
                          <div className="small muted">{meta.description}</div>
                          <div className="row wrap small mt-8" style={{ gap: 6 }}>
                            <span className="badge">{COPILOT[meta.copilot]}</span>
                            {r.shadow && <span className="badge amber">Só observando</span>}
                            {(r.globallyDisabled || r.globallyShadow) && <span className="badge red">Pausada pela equipe do Seu Impulso</span>}
                            {locked && <span className="badge green">Sempre ligada</span>}
                          </div>
                        </div>
                        <Switch on={r.enabled} label={`Ligar ${meta.title}`} disabled={!canEdit || locked || saving} onChange={(v) => onChange({ [r.id]: { enabled: v } })} />
                      </div>
                      {canEdit && !locked && (
                        <div className="row wrap" style={{ gap: 8 }}>
                          <button className="btn ghost sm" disabled={saving || !r.enabled} onClick={() => onChange({ [r.id]: { shadow: !r.shadow } })}>
                            {r.shadow ? 'Deixar agir' : 'Só observar'}
                          </button>
                          {meta.paramMeta.length > 0 && (
                            <button className="btn ghost sm" aria-expanded={isOpen} onClick={() => { setOpen(isOpen ? null : r.id); setDraft({ ...r.params }) }}>
                              {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />} Ajustar
                            </button>
                          )}
                        </div>
                      )}
                      {isOpen && (
                        <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none', padding: 14 }}>
                          <div className="grid g-2" style={{ gap: 12 }}>
                            {meta.paramMeta.map((pm) => (
                              <div className="field" key={pm.key}>
                                <label htmlFor={`${r.id}-${pm.key}`}>{pm.label}{pm.unit ? ` (${pm.unit})` : ''}</label>
                                <input id={`${r.id}-${pm.key}`} className="input" type="number" min={0} step="any" value={draft[pm.key] ?? ''} onChange={(e) => setDraft({ ...draft, [pm.key]: Number(e.target.value) })} />
                              </div>
                            ))}
                          </div>
                          <div className="row wrap mt-12" style={{ gap: 8 }}>
                            <button className="btn primary sm" disabled={saving} onClick={() => { onChange({ [r.id]: { params: draft } }); setOpen(null) }}>Salvar</button>
                            <button className="btn ghost sm" onClick={() => onChange({ [r.id]: { params: { ...meta.params } } })}>Voltar ao padrão</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </details>
          )
        })}
      </div>
    </div>
  )
}
